import React from "react"
import {Trans, useTranslation} from "react-i18next"
import {
  ArrowPathIcon,
  CalendarIcon,
  ChatBubbleOvalLeftIcon,
  ChevronDoubleRightIcon,
  RocketLaunchIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline"
import {CheckIcon} from "@heroicons/react/24/solid"

import {ExpandableBox} from "../../../components/ExpandableBox.tsx"
import {getCurrentLanguage} from "../../../i18n.ts"
import {
  ASalesAssistantUpdateCenterActivityCreateStep,
  ASalesAssistantUpdateCenterActivityDoneStep,
  ASalesAssistantUpdateCenterActivityRescheduleStep,
  ASalesAssistantUpdateCenterDealLostStep,
  ASalesAssistantUpdateCenterDealWonStep,
  ASalesAssistantUpdateCenterNoteStep,
  ASalesAssistantUpdateCenterStageStep,
  ASalesAssistantUpdateCenterStepTypeValues,
} from "../../../services/types.generated.ts"
import {apiDateToJS} from "../../../utils/dateArithmetics.ts"
import {commonTransComponents} from "../../../utils/i18n.tsx"
import {enumTranslKey} from "../../../utils/i18n.tsx"

export type TStep =
  | ASalesAssistantUpdateCenterNoteStep
  | ASalesAssistantUpdateCenterStageStep
  | ASalesAssistantUpdateCenterActivityDoneStep
  | ASalesAssistantUpdateCenterActivityCreateStep
  | ASalesAssistantUpdateCenterActivityRescheduleStep
  | ASalesAssistantUpdateCenterDealWonStep
  | ASalesAssistantUpdateCenterDealLostStep

const DATE_ONLY = /^\d{4}-\d{1,2}-\d{1,2}$/

/**
 * Activity date in `timeZone` (the browser's zone when omitted). A date without a time is a calendar date and is
 * shown as is.
 */
const formatActivityDate = (value: string, timeZone: string | undefined) =>
  apiDateToJS(value).toLocaleString(getCurrentLanguage(), {
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: DATE_ONLY.test(value) ? undefined : timeZone,
  })

/** Compact steps: 22px icons and smaller text in a 30px column, for the Klara dashboard feed. */
const CompactContext = React.createContext(false)

export const StepCard: React.FC<{step: TStep; timeZone?: string; compact?: boolean}> = ({
  step,
  timeZone,
  compact = false,
}) => <CompactContext value={compact}>{renderStep(step, timeZone)}</CompactContext>

const renderStep = (step: TStep, timeZone: string | undefined) => {
  switch (step.step_type) {
    case ASalesAssistantUpdateCenterStepTypeValues.Note:
      return <NoteStep step={step} />
    case ASalesAssistantUpdateCenterStepTypeValues.Stage:
      return <StageStep step={step} />
    case ASalesAssistantUpdateCenterStepTypeValues.ActivityDone:
      return <ActivityDoneStep step={step} />
    case ASalesAssistantUpdateCenterStepTypeValues.ActivityCreate:
      return <ActivityCreateStep step={step} timeZone={timeZone} />
    case ASalesAssistantUpdateCenterStepTypeValues.ActivityReschedule:
      return <ActivityRescheduleStep step={step} timeZone={timeZone} />
    case ASalesAssistantUpdateCenterStepTypeValues.DealWon:
      return <WonStep step={step} />
    case ASalesAssistantUpdateCenterStepTypeValues.DealLost:
      return <LostStep step={step} />
    default:
      return null
  }
}

const NoteStep: React.FC<{step: ASalesAssistantUpdateCenterNoteStep}> = ({step}) => {
  const {t} = useTranslation()
  const compact = React.useContext(CompactContext)

  if (compact) {
    return (
      <StepShell Icon={ChatBubbleOvalLeftIcon}>
        <span>{t("SalesAssistant_UpdateCenter_Steps_NoteTaken")}:</span>
        {step.body && (
          <div
            className={
              "mt-2 rounded-lg border border-cr-blue-light bg-cr-blue-super-light px-3.5 py-2.5 text-[11.5px] leading-relaxed text-cr-slate-90"
            }
          >
            <ExpandableBox lineClamp={4}>{step.body}</ExpandableBox>
          </div>
        )}
      </StepShell>
    )
  }

  return (
    <>
      <div className={"flex items-center justify-start pl-4"}>
        <div className={"h-4 w-px bg-indigo-100"}></div>
      </div>
      <div className={"flex items-stretch justify-start gap-3 self-stretch pl-[4px]"}>
        <div className={"flex flex-col items-center"}>
          <div className={"flex size-6 shrink-0 items-center justify-center rounded-full bg-indigo-200"}>
            <ChatBubbleOvalLeftIcon className={"size-3.5 text-indigo-500"} />
          </div>
          <div className={"w-px grow bg-indigo-100"}></div>
        </div>
        <div className={"flex min-w-0 flex-1 flex-col items-start justify-start gap-1"}>
          <div className={"w-full justify-start font-['Sora'] text-sm leading-5 font-normal text-gray-900"}>
            {t("SalesAssistant_UpdateCenter_Steps_NoteTaken")}:
          </div>
          {step.body && (
            <div className={"self-stretch rounded-lg bg-slate-50 p-4 outline outline-indigo-100"}>
              <ExpandableBox lineClamp={4}>
                <span className={"font-['Sora'] text-xs leading-4 font-normal tracking-tight text-gray-900"}>
                  {step.body}
                </span>
              </ExpandableBox>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

/**
 * One step's icon and text. The default is the sales-person page's timeline row; the compact one lines the icons up in
 * a 30px column with smaller text.
 */
const StepShell: React.FC<{Icon: React.ComponentType<{className?: string}>; children: React.ReactNode}> = ({
  Icon,
  children,
}) => {
  const compact = React.useContext(CompactContext)

  if (compact) {
    return (
      <div className={"relative grid grid-cols-[30px_minmax(0,1fr)] items-start gap-2 py-1.5"}>
        <span
          className={"ml-1 flex size-[22px] items-center justify-center rounded-full bg-cr-blue-light text-cr-blue-mid"}
        >
          <Icon className={"size-3"} />
        </span>
        {/* The step texts set their own size and weight; here they are scaled down and the bold parts darkened. */}
        <div
          className={
            "min-w-0 pt-0.5 text-[12.5px] text-cr-grey-80 [&_span]:text-[12.5px] [&_span]:leading-normal [&_span]:text-cr-grey-80 [&_span.font-bold]:text-cr-slate-90"
          }
        >
          {children}
        </div>
      </div>
    )
  }

  return (
    <>
      <div className={"flex items-center justify-start pl-4"}>
        <div className={"h-4 w-px bg-indigo-100"}></div>
      </div>
      <div className={"flex items-center justify-start gap-3 pl-1"}>
        <div className={"flex size-6 shrink-0 items-center justify-center rounded-full bg-indigo-200"}>
          <Icon className={"size-3.5 text-indigo-500"} />
        </div>
        <div className={"min-w-0 flex-1 justify-start"}>{children}</div>
      </div>
    </>
  )
}

const StageStep: React.FC<{step: ASalesAssistantUpdateCenterStageStep}> = ({step}) => {
  const {t} = useTranslation()
  return (
    <StepShell Icon={ArrowPathIcon}>
      <span className={"font-['Sora'] text-sm leading-5 font-normal text-gray-900"}>
        {t("SalesAssistant_UpdateCenter_Steps_StageChanged")}
        {(step.from_stage || step.to_stage) && ":"}
        {step.from_stage && !step.to_stage && ` ${t("SalesAssistant_UpdateCenter_Steps_From")}`}
      </span>
      {step.from_stage && (
        <span className={"font-['Sora'] text-sm leading-5 font-bold text-gray-900"}> {step.from_stage} </span>
      )}
      {step.to_stage && (
        <span className={"font-['Sora'] text-sm leading-5 font-normal text-gray-900"}>
          {" "}
          {step.from_stage
            ? t("SalesAssistant_UpdateCenter_Steps_To")
            : t("SalesAssistant_UpdateCenter_Steps_MovedTo")}{" "}
        </span>
      )}
      {step.to_stage && (
        <span className={"font-['Sora'] text-sm leading-5 font-bold text-gray-900"}>{step.to_stage}</span>
      )}
    </StepShell>
  )
}

const ActivityDoneStep: React.FC<{step: ASalesAssistantUpdateCenterActivityDoneStep}> = ({step}) => {
  const {t} = useTranslation()
  return (
    <StepShell Icon={CheckIcon}>
      <span className={"font-['Sora'] text-sm leading-5 font-normal text-gray-900"}>
        {t("SalesAssistant_UpdateCenter_Steps_CollectedActivity")}:{" "}
      </span>
      {step.activity_type && (
        <span className={"font-['Sora'] text-sm leading-5 font-bold text-gray-900"}>
          {" "}
          {t(enumTranslKey("SalesAssistantTaskTypes", step.activity_type))}{" "}
        </span>
      )}
      <span className={"font-['Sora'] text-sm leading-5 font-normal text-gray-900"}>
        {t("SalesAssistant_UpdateCenter_Steps_MarkedAs")}:{" "}
      </span>
      <span className={"font-['Sora'] text-sm leading-5 font-bold text-gray-900"}>
        {t("SalesAssistant_UpdateCenter_Steps_Done")}
      </span>
    </StepShell>
  )
}

const ActivityCreateStep: React.FC<{
  step: ASalesAssistantUpdateCenterActivityCreateStep
  timeZone: string | undefined
}> = ({step, timeZone}) => {
  const {t} = useTranslation()
  const activityDate = formatActivityDate(step.created_at ?? "", timeZone)

  return (
    <StepShell Icon={ChevronDoubleRightIcon}>
      <span className={"font-['Sora'] text-sm leading-5 font-normal text-gray-900"}>
        {t("SalesAssistant_UpdateCenter_Steps_CreatedActivity")}:{" "}
      </span>
      {step.activity_type && (
        <span className={"font-['Sora'] text-sm leading-5 font-bold text-gray-900"}>
          {t(enumTranslKey("SalesAssistantTaskTypes", step.activity_type))}{" "}
        </span>
      )}
      {activityDate && (
        <>
          <span className={"font-['Sora'] text-sm leading-5 font-normal text-gray-900"}>
            {" "}
            {t("SalesAssistant_UpdateCenter_Steps_On")}{" "}
          </span>
          <span className={"font-['Sora'] text-sm leading-5 font-bold text-gray-900"}>{activityDate}</span>
        </>
      )}
    </StepShell>
  )
}

const ActivityRescheduleStep: React.FC<{
  step: ASalesAssistantUpdateCenterActivityRescheduleStep
  timeZone: string | undefined
}> = ({step, timeZone}) => {
  const {t} = useTranslation()
  const activityDate = step.scheduled_at ? formatActivityDate(step.scheduled_at, timeZone) : ""

  return (
    <StepShell Icon={CalendarIcon}>
      <span className={"font-['Sora'] text-sm leading-5 font-normal text-gray-900"}>
        {t("SalesAssistant_UpdateCenter_Steps_RescheduledActivity")}:{" "}
      </span>
      {step.activity_type && (
        <span className={"font-['Sora'] text-sm leading-5 font-bold text-gray-900"}>
          {" "}
          {t(enumTranslKey("SalesAssistantTaskTypes", step.activity_type))}{" "}
        </span>
      )}
      {activityDate && (
        <>
          <span className={"font-['Sora'] text-sm leading-5 font-normal text-gray-900"}>
            {" "}
            {t("SalesAssistant_UpdateCenter_Steps_On")}{" "}
          </span>
          <span className={"font-['Sora'] text-sm leading-5 font-bold text-gray-900"}>{activityDate}</span>
        </>
      )}
    </StepShell>
  )
}

const WonStep: React.FC<{step: ASalesAssistantUpdateCenterDealWonStep}> = () => {
  return (
    <StepShell Icon={RocketLaunchIcon}>
      <span className={"font-['Sora'] text-sm leading-5 font-normal text-gray-900"}>
        <Trans i18nKey={"SalesAssistant_UpdateCenter_Steps_DealWasWon"} components={commonTransComponents} />
      </span>
    </StepShell>
  )
}

const LostStep: React.FC<{step: ASalesAssistantUpdateCenterDealLostStep}> = () => {
  return (
    <StepShell Icon={XMarkIcon}>
      <span className={"font-['Sora'] text-sm leading-5 font-normal text-gray-900"}>
        <Trans i18nKey={"SalesAssistant_UpdateCenter_Steps_DealWasLost"} components={commonTransComponents} />
      </span>
    </StepShell>
  )
}
